import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

interface UiState {
  approveModalOpen: boolean;
  browseMode: boolean;
  sidebarOpen: boolean;
  displayMode: 'mobile' | 'tablet' | 'desktop';
  helpModalOpen: boolean;
  completeTasksModalOpen: boolean;
  shareModalOpen: boolean;
  lightboxOpen: boolean;
  lightboxImageUrl?: string;
  isScreenshotInProgress: boolean;
  focusedContainer: 'fixed' | 'scrollable' | null;
  showLoadingBar: boolean;
  progress: number;
  showTempMessage: boolean;
  inputValue: string;
  cursorsVisible: boolean;
  fullPageScreenshotId?: string; // Changed from fullPageScreenshot to store only the ID
  viewportScreenshotId?: string;
  pageThumbnail?: string;
  isPageLoading: boolean; // New state for page loading overlay
  isBottomBarLoading: boolean; // New state for bottom bar loading overlay
}

const initialState: UiState = {
  approveModalOpen: false,
  browseMode: false,
  sidebarOpen: true,
  displayMode: 'desktop',
  helpModalOpen: false,
  completeTasksModalOpen: false,
  shareModalOpen: false,
  lightboxOpen: false,
  lightboxImageUrl: undefined,
  isScreenshotInProgress: false,
  focusedContainer: 'fixed',
  showLoadingBar: false,
  progress: 0,
  showTempMessage: false,
  inputValue: '',
  cursorsVisible: false,
  fullPageScreenshotId: undefined,
  viewportScreenshotId: undefined,
  pageThumbnail: undefined,
  isPageLoading: true, // Start with page loading true
  isBottomBarLoading: true, // Start with bottom bar loading true
};

const uiSlice = createSlice({
  initialState,
  name: 'ui',
  reducers: {
    setApproveModalOpen: (state, action: PayloadAction<boolean>) => {
      state.approveModalOpen = action.payload;
    },
    setBrowseMode: (state, action: PayloadAction<boolean>) => {
      state.browseMode = action.payload;
    },
    setDisplayMode: (
      state,
      action: PayloadAction<'mobile' | 'tablet' | 'desktop'>
    ) => {
      state.displayMode = action.payload;
    },
    setHelpModalOpen: (state, action: PayloadAction<boolean>) => {
      state.helpModalOpen = action.payload;
    },
    setCompleteTasksModalOpen: (state, action: PayloadAction<boolean>) => {
      state.completeTasksModalOpen = action.payload;
    },
    setShareModalOpen: (state, action: PayloadAction<boolean>) => {
      state.shareModalOpen = action.payload;
    },
    setLightboxOpen: (state, action: PayloadAction<boolean>) => {
      state.lightboxOpen = action.payload;
    },
    setLightboxImageUrl: (state, action: PayloadAction<string | undefined>) => {
      state.lightboxImageUrl = action.payload;
    },
    setIsScreenshotInProgress: (state, action: PayloadAction<boolean>) => {
      state.isScreenshotInProgress = action.payload;
    },
    setFocusedContainer: (
      state,
      action: PayloadAction<'fixed' | 'scrollable' | null>
    ) => {
      state.focusedContainer = action.payload;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    startLoading: (state) => {
      state.showLoadingBar = true;
    },
    stopLoading: (state) => {
      state.showLoadingBar = false;
      state.progress = 0;
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    setShowTempMessage: (state, action: PayloadAction<boolean>) => {
      state.showTempMessage = action.payload;
    },
    setPageLoading: (state, action: PayloadAction<boolean>) => {
      state.isPageLoading = action.payload;
    },
    setBottomBarLoading: (state, action: PayloadAction<boolean>) => {
      state.isBottomBarLoading = action.payload;
    },
    setInputValue: (state, action: PayloadAction<string>) => {
      // Limit input value to 60 characters
      state.inputValue = action.payload.slice(0, 60);
    },
    setCursorsVisible: (state, action: PayloadAction<boolean>) => {
      state.cursorsVisible = action.payload;
    },
    setFullPageScreenshotId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.fullPageScreenshotId = action.payload;
    },
    setViewportScreenshotId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.viewportScreenshotId = action.payload;
    },
    setPageThumbnail: (state, action: PayloadAction<string | undefined>) => {
      state.pageThumbnail = action.payload;
    },
  },
});

export const {
  setApproveModalOpen,
  setBrowseMode,
  setDisplayMode,
  setHelpModalOpen,
  setCompleteTasksModalOpen,
  setShareModalOpen,
  setLightboxOpen,
  setLightboxImageUrl,
  setIsScreenshotInProgress,
  setFocusedContainer,
  setSidebarOpen,
  startLoading,
  stopLoading,
  setProgress,
  setShowTempMessage,
  setPageLoading,
  setBottomBarLoading,
  setInputValue,
  setCursorsVisible,
  setFullPageScreenshotId,
  setViewportScreenshotId,
  setPageThumbnail,
} = uiSlice.actions;
export const getApproveModalOpen = (state: RootState) =>
  state.ui.approveModalOpen;
export const getBrowseMode = (state: RootState) => state.ui.browseMode;
export const getDisplayMode = (state: RootState) => state.ui.displayMode;
export const getHelpModalOpen = (state: RootState) => state.ui.helpModalOpen;
export const getCompleteTasksModalOpen = (state: RootState) =>
  state.ui.completeTasksModalOpen;
export const getShareModalOpen = (state: RootState) => state.ui.shareModalOpen;
export const getLightboxOpen = (state: RootState) => state.ui.lightboxOpen;
export const getLightboxImageUrl = (state: RootState) =>
  state.ui.lightboxImageUrl;
export const getIsScreenshotInProgress = (state: RootState) =>
  state.ui.isScreenshotInProgress;
export const getFocusedContainer = (state: RootState) =>
  state.ui.focusedContainer;
export const getSidebarOpen = (state: RootState) => state.ui.sidebarOpen;
export const getShowTempMessage = (state: RootState) =>
  state.ui.showTempMessage;
export const getInputValue = (state: RootState) => state.ui.inputValue;
export const getCursorsVisible = (state: RootState) => state.ui.cursorsVisible;
export const getFullPageScreenshotId = (state: RootState) =>
  state.ui.fullPageScreenshotId;
export const getViewportScreenshotId = (state: RootState) =>
  state.ui.viewportScreenshotId;
export const getPageThumbnail = (state: RootState) => state.ui.pageThumbnail;
export const getIsPageLoading = (state: RootState) => state.ui.isPageLoading;
export const getIsBottomBarLoading = (state: RootState) =>
  state.ui.isBottomBarLoading;

// Input selectors for loading bar
const getShowLoadingBar = (state: RootState) => state.ui.showLoadingBar;
const getLoadingProgress = (state: RootState) => state.ui.progress;

// Memoized selectors
export const getLoadingBar = createSelector(
  [getShowLoadingBar, getLoadingProgress],
  (show, progress) => ({
    show,
    progress,
  })
);
export default uiSlice.reducer;
